import { type Ref, computed } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'

import { useBuybackConfig } from '../../../../composables/config/useBuybackConfig'

import { useBuybackOffer } from './useBuybackOffer'

type DiscountableElement = { price: Price; priceNew?: Price }

/**
 * Enriches product elements with a `discountedPrice` (price after applying
 * the user's trade-in offer) when the product is eligible.
 */
export function useDiscountedPrice<T extends DiscountableElement>(
  elements: Ref<T[] | T | null | undefined>,
) {
  const { getDiscountedPriceForProductCard } = useBuybackOffer()
  const { user } = useUserStore()
  const { requiresNewPriceForTradeInDiscount } = useBuybackConfig()

  function withDiscountedPrice(element: T) {
    const isEligible =
      user.value.cartItemsCount === 0 &&
      (!requiresNewPriceForTradeInDiscount || Boolean(element.priceNew))

    return {
      ...element,
      discountedPrice: isEligible
        ? getDiscountedPriceForProductCard(element.price)
        : undefined,
    }
  }

  const products = computed(() =>
    Array.isArray(elements.value)
      ? elements.value.map(withDiscountedPrice)
      : [],
  )

  const product = computed(() =>
    elements.value && !Array.isArray(elements.value)
      ? withDiscountedPrice(elements.value)
      : null,
  )

  return { products, product }
}
